import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { InitialAvatar, getBadgeClass } from "../../../config/utils";
import Select from "react-select";
import AdminUpdateChecktime from "../../../components/Modal/AdminUpdateChecktime";

function DailyReport() {
  const isInitialLoad = useRef(true);
  const role = localStorage.getItem("role");

  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyAbsent, setShowOnlyAbsent] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [checktimeModal, setChecktimeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const fetchDailyReport = async (
    date = selectedDate,
    shift = selectedShift?.value,
    department = selectedDepartment?.value
  ) => {
    try {
      setLoading(true);
      const shiftParam = shift === "all" ? "" : `&shift=${shift}`;
      const departmentParam = department ? `&department_id=${department}` : "";
      const response = await api.get(
        `/daily-report?date=${date}${shiftParam}${departmentParam}`
      );
      const data = response.data || [];
      setReportData(data);
      setShowTable(true);
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach((err) => {
          toast.error(err);
        });
      } else {
        toast.error("Failed to fetch attendance data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchShiftList = async () => {
    try {
      const response = await api.get("/shifts");
      const shiftOptions = response.data.data.map((shift) => ({
        value: shift.id,
        label: shift.shift_name,
      }));
      const allShiftsOption = { value: "all", label: "All" };
      const shiftsWithAll = [allShiftsOption, ...shiftOptions];
      setShifts(shiftsWithAll);
      setSelectedShift(allShiftsOption);
    } catch (error) {
      toast.error("Failed to fetch shifts");
    }
  };

  const fetchDepartmentList = async () => {
    try {
      const response = await api.get("/departments");
      const deptOptions = (response.data.data || []).map((dept) => ({
        value: dept.department_id,
        label: dept.department_name,
      }));
      const allDeptOption = { value: "", label: "All" };
      setDepartments([allDeptOption, ...deptOptions]);
      setSelectedDepartment(allDeptOption);
    } catch (error) {
      toast.error("Failed to fetch departments");
    }
  };

  useEffect(() => {
    fetchShiftList();
    fetchDepartmentList();
  }, []);
  useEffect(() => {
    if (selectedShift && selectedDepartment !== null && isInitialLoad.current) {
      fetchDailyReport();
      isInitialLoad.current = false;
    }
  }, [selectedShift, selectedDepartment]);

  const handleSearch = () => {
    if (!selectedDate) return;
    setLoading(true);
    setShowTable(false);
    fetchDailyReport();
  };

  const openChecktimeModal = (employee, type) => {
    if (role === "admin") {
      setSelectedEmployee({ ...employee, check_type: type });
      setChecktimeModal(true);
    }
  };

  const handlePrintPage = () => {
    if (!selectedDate || !selectedShift?.value) {
      toast.error("Invalid parameters for printing");
      return;
    }
    const deptParam = selectedDepartment?.value ? `&department_id=${selectedDepartment.value}` : "";
    const printUrl = `/user/daily-report/print?date=${selectedDate}&shift=${selectedShift?.value}${deptParam}`;
    window.open(printUrl, "_blank");
  };

  const filteredEmployees = reportData?.employees_attendance?.filter(
    (employee) => {
      const matchesSearch = employee.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAbsent = !showOnlyAbsent || employee.status?.toLowerCase() === "absent";
      return matchesSearch && matchesAbsent;
    }
  );

  return (
    <>
      <style>
        {`
          .table td, .table th {
            vertical-align: middle;
          }
        `}
      </style>
      <main id="main" className="main">
      <div className="pagetitle">
        <h1>
          Daily Report
          <i
            className="bi bi-info-circle ms-2"
            style={{ cursor: "pointer", fontSize: "1rem" }}
            data-bs-toggle="tooltip"
            data-bs-placement="right"
            title="View and manage employee attendance records with filters, search, and export options."
          ></i>
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Home</Link>
            </li>
            <li className="breadcrumb-item active">Daily Report</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                
                <div className="row g-2  mt-3">
                  <div className="col-md-3">
                    <label className="form-label small text-muted">Search Employee</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search employee"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small text-muted">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small text-muted">Shift</label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      name="shift"
                      options={shifts.map((shift) => ({
                        value: shift.value,
                        label: shift.label,
                      }))}
                      value={selectedShift}
                      onChange={(selectedOption) =>
                        setSelectedShift(selectedOption)
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small text-muted">Department</label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      name="department"
                      options={departments.map((dept) => ({
                        value: dept.value,
                        label: dept.label,
                      }))}
                      value={selectedDepartment}
                      onChange={(selectedOption) =>
                        setSelectedDepartment(selectedOption)
                      }
                      placeholder="Select department"
                    />
                  </div>
                  <div className="col-md-3 d-flex justify-content-between align-items-end gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleSearch}
                      disabled={!selectedDate || loading}
                    >
                      {loading ? "Searching..." : "Search"}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handlePrintPage()}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Print Report"
                    >
                      Print Report
                    </button>
                  </div>

                </div>
                <div className="row g-2 mb-3 ">
                  <div className="col-md-12">
                  <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="showOnlyAbsent"
                        checked={showOnlyAbsent}
                        onChange={(e) => setShowOnlyAbsent(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="showOnlyAbsent">
                        Only show absent
                      </label>
                    </div>
                  </div>
                </div>

                {loading && (
                  <div className="text-center mt-3">
                    <Spinner animation="border" variant="primary" />
                    <span className="ms-2">Loading...</span>
                  </div>
                )}

                {showTable && !loading && (
                  <div className="table-responsive mt-3">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Department</th>
                          {/* <th>Shift Time</th> */}
                          <th>Check-in at</th>
                          <th>Check-out at</th>
                          <th>Status</th>
                          <th>Total Hours</th>
                          <th>Overtime</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees?.length > 0 ? (
                          <>
                            {filteredEmployees.map((data, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    {data?.profile_picture ? (
                                      <img
                                        src={data.profile_picture}
                                        alt={data.employee_name}
                                        style={{
                                          width: "30px",
                                          height: "30px",
                                          borderRadius: "50%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <InitialAvatar
                                        name={data?.employee_name}
                                      />
                                    )}
                                    <Link 
                                      to={`/user/report/${data?.employee_id}?month=${new Date(selectedDate).getMonth() + 1}&year=${new Date(selectedDate).getFullYear()}`}
                                      className="text-primary text-decoration-none"
                                      target="_blank"
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {data?.employee_name || "--"}
                                      
                                      <p className="text-muted" style={{ marginBottom: '0px' }}>
                                        ({data?.employee_role || "--"})
                                      </p>
                                    </Link>
                                  </div>
                                </td>
                                <td>{data?.department_name || "--"}</td>
                                {/* <td>{data?.shift_time || "--"}</td> */}
                                <td>
                                  <span
                                    onClick={
                                      role === "admin"
                                        ? () =>
                                            openChecktimeModal(data, "Check In")
                                        : undefined
                                    }
                                    className={`d-flex align-items-center gap-1 c-pointer ${
                                      data.check_in && role === "admin"
                                        ? "text-primary"
                                        : ""
                                    }`}
                                  >
                                    {data.check_in || "--"}
                                    {role === "admin" && (
                                      <i className="bi bi-pencil font-12"></i>
                                    )}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    onClick={
                                      data.check_in && role === "admin"
                                        ? () =>
                                            openChecktimeModal(
                                              data,
                                              "Check Out"
                                            )
                                        : undefined
                                    }
                                    className={`d-flex align-items-center gap-1 ${
                                      data.check_in ? "c-pointer" : "c-auto"
                                    } ${
                                      data.check_out && role === "admin"
                                        ? "text-primary"
                                        : ""
                                    }`}
                                  >
                                    {data.check_out || "--"}
                                    {data.check_in && role === "admin" && (
                                      <i className="bi bi-pencil font-12"></i>
                                    )}
                                    <small className="text-muted">
                                      {data.source || ""}
                                    </small>
                                  </span>
                                </td>
                                <td
                                  className={`text-${getBadgeClass(
                                    data.status
                                  )}`}
                                >
                                  {data?.status}
                                </td>
                                <td >
                                  {data.total_hours || "--"}
                                  <p style={{ color: "red", marginBottom: '0px' }}>
                                    {data.short_hours && data.check_out 
                                      ? " (-"+data.short_hours +")"
                                      : ""}
                                  </p>
                                </td>
                                <td>{data.overtime || "--"}</td>
                              </tr>
                            ))}

                            {reportData?.summary && (
                              <tr className="fw-bold table-active">
                                <td colSpan="5" className="">
                                  Totals:
                                </td>
                                <td>
                                  {reportData.summary.total_hours || "--"}
                                  
                                </td>
                                <td>
                                  {reportData.summary.total_overtime || "--"}
                                </td>
                              </tr>
                            )}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">
                              No record found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <AdminUpdateChecktime
        show={checktimeModal}
        onClose={() => setChecktimeModal(false)}
        currentEmployee={selectedEmployee}
        employeeId={selectedEmployee?.employee_id}
        onAttendanceUpdated={() =>
          fetchDailyReport(selectedDate, selectedShift?.value, selectedDepartment?.value)
        }
      />
    </main>
    </>
  );
}

export default DailyReport;
